/**
 * @fileoverview Componente responsável por editar opções gerais e preferências da aplicação.
 * Layout compacto em seções com itens inline (padrão SaaS settings list).
 */

import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import { useTheme } from "@material-ui/core/styles";
import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

import {
  ClockFading,
  Zap,
  ChartGantt,
  BellRing,
  MessageSquare,
  MessagesSquare,
  Settings,
  BarChart3,
  Phone,
  PhoneOff,
  Send,
  Gauge,
  Timer,
  Clock,
  GraduationCap,
} from "lucide-react";

import SettingsSection from "./SettingsSection";
import SettingItem from "./SettingItem";

const INTERVAL_UNITS = [
  { value: "minutes", label: "Minutos" },
  { value: "hours", label: "Horas" },
  { value: "days", label: "Dias" },
];

export default function Options(props) {
  const { settings, scheduleTypeChanged, currentUser } = props;

  const [scheduleType, setScheduleType] = useState("disabled");
  const [quickMessagesScope, setQuickMessagesScope] = useState("user");
  const [noResponseIndicator, setNoResponseIndicator] = useState("disabled");
  const [enableAcademy, setEnableAcademy] = useState("enabled");
  const [noResponseBusinessHours, setNoResponseBusinessHours] = useState("disabled");
  const [unrepliedClockIcon, setUnrepliedClockIcon] = useState("disabled");
  const [webNotifications, setWebNotifications] = useState("disabled");
  const [internalChatNotifications, setInternalChatNotifications] = useState("enabled");


  // SMS Alert state
  const [smsEnabled, setSmsEnabled] = useState("disabled");
  const [smsThreshold, setSmsThreshold] = useState("20");
  const [smsIntervalValue, setSmsIntervalValue] = useState("20");
  const [smsIntervalUnit, setSmsIntervalUnit] = useState("minutes");
  const [smsPhones, setSmsPhones] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const phonesTimerRef = useRef(null);

  // Call rejection state
  const [callRejectionEnabled, setCallRejectionEnabled] = useState("disabled");
  const [callRejectionMessage, setCallRejectionMessage] = useState("");
  const callMessageTimerRef = useRef(null);

  // ART thresholds state
  const [artYellow, setArtYellow] = useState("10");
  const [artRed, setArtRed] = useState("20");
  const [artError, setArtError] = useState(null);
  const artYellowTimer = useRef(null);
  const artRedTimer = useRef(null);

  const { update } = useSettings();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const isAdminProfile = (currentUser?.profile || "").toLowerCase() === "admin";

  // Estilo compartilhado para inputs dentro do bloco SMS
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      color: isDark ? "#FAFAFA" : "#1e293b",
      backgroundColor: isDark ? "rgba(39, 39, 42, 0.6)" : "#fff",
      "& fieldset": {
        borderColor: isDark ? "rgba(91, 124, 255, 0.2)" : "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: isDark ? "rgba(91, 124, 255, 0.5)" : "#3D5CFD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#5B7CFF",
      },
    },
    "& .MuiInputBase-input": {
      color: isDark ? "#FAFAFA" : "#1e293b",
    },
    "& .MuiInputBase-input::placeholder": {
      color: isDark ? "#71717A" : "#94a3b8",
      opacity: 1,
    },
    // Label flutuante (ex.: "Limite amarelo"/"Limite vermelho" no card ART) —
    // sem isto cai na cor padrão do MUI, que fica preta no modo escuro.
    "& .MuiInputLabel-root": {
      color: isDark ? "#A1A1AA" : "#64748b",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: isDark ? "#5B7CFF" : "#3D5CFD",
    },
    "& .MuiSvgIcon-root": {
      color: isDark ? "#A1A1AA" : "#64748b",
    },
  };

  useEffect(() => {
    if (Array.isArray(settings) && settings.length) {
      const scheduleTypeSetting = settings.find((s) => s.key === "scheduleType");
      if (scheduleTypeSetting) setScheduleType(scheduleTypeSetting.value);

      const qmSetting = settings.find((s) => s.key === "quickMessagesScope");
      if (qmSetting) setQuickMessagesScope(qmSetting.value);

      const nrSetting = settings.find((s) => s.key === "noResponseIndicator");
      if (nrSetting) setNoResponseIndicator(nrSetting.value);

      const academySetting = settings.find((s) => s.key === "enableAcademy");
      if (academySetting) setEnableAcademy(academySetting.value);

      const bhSetting = settings.find((s) => s.key === "noResponseBusinessHours");
      if (bhSetting) setNoResponseBusinessHours(bhSetting.value);

      const ucSetting = settings.find((s) => s.key === "unrepliedClockIcon");
      if (ucSetting) setUnrepliedClockIcon(ucSetting.value);

      const wnSetting = settings.find((s) => s.key === "webNotifications");
      if (wnSetting) setWebNotifications(wnSetting.value);

      const icnSetting = settings.find((s) => s.key === "internalChatNotifications");
      if (icnSetting) setInternalChatNotifications(icnSetting.value);

      // SMS
      const smsEn = settings.find((s) => s.key === "smsAlertEnabled");
      if (smsEn) setSmsEnabled(smsEn.value);

      const smsTh = settings.find((s) => s.key === "smsAlertThreshold");
      if (smsTh) setSmsThreshold(smsTh.value);

      const smsIv = settings.find((s) => s.key === "smsAlertIntervalValue");
      if (smsIv) setSmsIntervalValue(smsIv.value);

      const smsIu = settings.find((s) => s.key === "smsAlertIntervalUnit");
      if (smsIu) setSmsIntervalUnit(smsIu.value);

      try {
        const smsPh = settings.find((s) => s.key === "smsReportPhones");
        if (smsPh) {
          const phones = JSON.parse(smsPh.value);
          setSmsPhones(phones.map((p) => formatPhoneDisplay(p)).join("\n"));
        }
      } catch {}

      // Call rejection
      const callRejEn = settings.find((s) => s.key === "callRejectionEnabled");
      if (callRejEn) setCallRejectionEnabled(callRejEn.value);

      const callRejMsg = settings.find((s) => s.key === "callRejectionMessage");
      if (callRejMsg) setCallRejectionMessage(callRejMsg.value);

      // ART thresholds
      const artY = settings.find((s) => s.key === "artYellowThresholdMinutes");
      if (artY) setArtYellow(String(artY.value));

      const artR = settings.find((s) => s.key === "artRedThresholdMinutes");
      if (artR) setArtRed(String(artR.value));
    }
  }, [settings]);

  // === Handlers ===

  // Persiste setting em background. Atualização visual é imediata (optimistic update).
  function saveSetting(key, value) {
    update({ key, value }).catch(() => {
      toast.error("Erro ao salvar configuração");
    });
  }

  function handleEnableAcademy(value) {
    setEnableAcademy(value);
    saveSetting("enableAcademy", value);
  }

  function handleScheduleType(value) {
    setScheduleType(value);
    saveSetting("scheduleType", value);
    if (typeof scheduleTypeChanged === "function") {
      scheduleTypeChanged(value);
    }
  }

  function handleQuickMessagesScope(value) {
    setQuickMessagesScope(value);
    saveSetting("quickMessagesScope", value);
  }

  function handleNoResponseIndicator(value) {
    setNoResponseIndicator(value);
    saveSetting("noResponseIndicator", value);
  }

  function handleNoResponseBusinessHours(value) {
    setNoResponseBusinessHours(value);
    saveSetting("noResponseBusinessHours", value);
  }

  function handleUnrepliedClockIcon(value) {
    setUnrepliedClockIcon(value);
    saveSetting("unrepliedClockIcon", value);
  }

  async function handleWebNotifications(value) {
    if (value === "enabled") {
      if (!("Notification" in window)) {
        toast.error(i18n.t("settings.options.toasts.webNotificationsUnsupported"));
        return;
      }
      let permission = Notification.permission;
      if (permission !== "granted") {
        permission = await Notification.requestPermission();
      }
      if (permission !== "granted") {
        toast.warn(i18n.t("settings.options.toasts.webNotificationsPermissionDenied"));
        return;
      }
    }

    setWebNotifications(value);
    saveSetting("webNotifications", value);
  }

  function handleInternalChatNotifications(value) {
    setInternalChatNotifications(value);
    saveSetting("internalChatNotifications", value);
  }

  // === Phone formatting ===
  const sanitizePhone = (raw) => {
    let digits = String(raw || "").replace(/\D/g, "");
    if (digits.startsWith("0")) digits = digits.substring(1);
    if (digits.length > 0 && !digits.startsWith("55")) digits = "55" + digits;
    if (digits.length > 13) digits = digits.substring(0, 13);
    return digits;
  };

  const formatPhoneDisplay = (raw) => {
    const digits = sanitizePhone(raw);
    if (digits.length === 0) return "";
    if (digits.length <= 2) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.substring(0, 2)} (${digits.substring(2)}`;
    if (digits.length <= 9)
      return `+${digits.substring(0, 2)} (${digits.substring(2, 4)}) ${digits.substring(4)}`;
    if (digits.length <= 13) {
      const ddd = digits.substring(2, 4);
      const firstPart = digits.substring(4, digits.length - 4);
      const lastPart = digits.substring(digits.length - 4);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    return digits;
  };

  // === SMS handlers ===
  function handleSmsEnabled(value) {
    setSmsEnabled(value);
    saveSetting("smsAlertEnabled", value);
  }

  function handleSmsThreshold(e) {
    const val = e.target.value;
    setSmsThreshold(val);
    saveSetting("smsAlertThreshold", val);
  }

  function handleSmsIntervalValue(e) {
    const val = e.target.value;
    setSmsIntervalValue(val);
    saveSetting("smsAlertIntervalValue", val);
  }

  function handleSmsIntervalUnit(e) {
    const val = e.target.value;
    setSmsIntervalUnit(val);
    saveSetting("smsAlertIntervalUnit", val);
  }

  function handlePhonesChange(e) {
    const raw = e.target.value;
    setSmsPhones(raw);
    if (phonesTimerRef.current) clearTimeout(phonesTimerRef.current);
    phonesTimerRef.current = setTimeout(() => {
      const phones = raw
        .split("\n")
        .map((p) => sanitizePhone(p))
        .filter(Boolean);
      update({ key: "smsReportPhones", value: JSON.stringify(phones) });
    }, 1000);
  }

  function handlePhonesBlur() {
    const lines = smsPhones.split("\n").filter((l) => l.trim());
    const formatted = lines.map((l) => formatPhoneDisplay(l));
    setSmsPhones(formatted.join("\n"));
    const phones = lines.map((l) => sanitizePhone(l)).filter(Boolean);
    update({ key: "smsReportPhones", value: JSON.stringify(phones) });
  }

  // === Call rejection handlers ===
  function handleCallRejectionEnabled(value) {
    setCallRejectionEnabled(value);
    saveSetting("callRejectionEnabled", value);
  }

  function handleCallRejectionMessageChange(e) {
    const raw = e.target.value;
    setCallRejectionMessage(raw);
    if (callMessageTimerRef.current) clearTimeout(callMessageTimerRef.current);
    callMessageTimerRef.current = setTimeout(() => {
      update({ key: "callRejectionMessage", value: raw }).catch(() => {
        toast.error("Erro ao salvar mensagem de chamada");
      });
    }, 1000);
  }

  function handleCallRejectionMessageBlur() {
    if (callMessageTimerRef.current) clearTimeout(callMessageTimerRef.current);
    update({ key: "callRejectionMessage", value: callRejectionMessage }).catch(() => {
      toast.error("Erro ao salvar mensagem de chamada");
    });
  }

  // === ART thresholds handlers ===
  function validateArtPair(yellowStr, redStr) {
    const y = Number(yellowStr);
    const r = Number(redStr);
    if (!Number.isInteger(y) || !Number.isInteger(r)) {
      return "Informe valores inteiros";
    }
    if (y < 1 || y > 1440 || r < 1 || r > 1440) {
      return "Valores devem estar entre 1 e 1440 minutos";
    }
    if (y >= r) {
      return "O limite amarelo deve ser menor que o vermelho";
    }
    return null;
  }

  function handleArtYellowChange(e) {
    const val = e.target.value;
    setArtYellow(val);
    const err = validateArtPair(val, artRed);
    if (err) {
      setArtError(err);
      return;
    }
    setArtError(null);
    if (artYellowTimer.current) clearTimeout(artYellowTimer.current);
    artYellowTimer.current = setTimeout(() => {
      saveSetting("artYellowThresholdMinutes", val);
    }, 1000);
  }

  function handleArtRedChange(e) {
    const val = e.target.value;
    setArtRed(val);
    const err = validateArtPair(artYellow, val);
    if (err) {
      setArtError(err);
      return;
    }
    setArtError(null);
    if (artRedTimer.current) clearTimeout(artRedTimer.current);
    artRedTimer.current = setTimeout(() => {
      saveSetting("artRedThresholdMinutes", val);
    }, 1000);
  }

  async function handleTestSMS() {
    setTestLoading(true);
    try {
      const { data } = await api.post("/sms-report/test");
      if (data.success) {
        toast.success(`SMS enviado para ${data.phone}!`);
      } else {
        toast.error(`Erro ao enviar: ${data.error}`);
      }
    } catch (err) {
      toastError(err);
    }
    setTestLoading(false);
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
        gap: 2,
        alignItems: "start",
      }}
    >
      {/* Coluna ESQUERDA — cada coluna é um flex independente; no grid por
          linhas as alturas ficavam acopladas (o card mais alto da linha abria
          um buraco abaixo do vizinho, ex.: ART × Notificações) */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>

      {/* ===== CONFIGURAÇÕES GERAIS ===== */}
      <SettingsSection icon={Settings} title="Configurações Gerais">
        <SettingItem
          icon={ClockFading}
          title={i18n.t("settings.options.fields.expedientManager.title")}
          description="Controle o gerenciamento de expediente"
          value={scheduleType}
          onChange={handleScheduleType}

          options={[
            { value: "disabled", label: i18n.t("settings.options.fields.disabled") },
            { value: "queue", label: i18n.t("settings.options.fields.expedientManager.queue") },
            { value: "company", label: i18n.t("settings.options.fields.expedientManager.company") },
          ]}
        />
        <SettingItem
          icon={Zap}
          title={i18n.t("settings.options.fields.quickMessagesScope.title")}
          description={i18n.t("settings.options.fields.quickMessagesScope.description")}
          value={quickMessagesScope}
          onChange={handleQuickMessagesScope}

          options={[
            { value: "user", label: i18n.t("settings.options.fields.quickMessagesScope.user") },
            { value: "company", label: i18n.t("settings.options.fields.quickMessagesScope.company") },
          ]}
        />
        <SettingItem
          icon={GraduationCap}
          title={i18n.t("settings.options.fields.enableAcademy.title")}
          description={i18n.t("settings.options.fields.enableAcademy.description")}
          value={enableAcademy}
          onChange={handleEnableAcademy}
          divider={false}
          toggle
        />
      </SettingsSection>

      {/* ===== LIMITES DE ART ===== */}
      <SettingsSection
        icon={Timer}
        title={i18n.t("settings.options.fields.artThresholds.title")}
      >
        <SettingItem
          icon={Timer}
          title={i18n.t("settings.options.fields.artThresholds.title")}
          description={i18n.t("settings.options.fields.artThresholds.description")}
          divider={false}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              type="number"
              label={i18n.t("settings.options.fields.artThresholds.yellowLabel")}
              value={artYellow}
              onChange={handleArtYellowChange}
              inputProps={{ min: 1, max: 1440 }}
              variant="outlined"
              size="small"
              error={!!artError}
              helperText={artError || " "}
              sx={{ width: 130, ...fieldSx }}
            />
            <TextField
              type="number"
              label={i18n.t("settings.options.fields.artThresholds.redLabel")}
              value={artRed}
              onChange={handleArtRedChange}
              inputProps={{ min: 1, max: 1440 }}
              variant="outlined"
              size="small"
              error={!!artError}
              helperText={artError ? " " : " "}
              sx={{ width: 130, ...fieldSx }}
            />
          </Box>
        </SettingItem>
      </SettingsSection>

      {/* ===== CHAMADAS RECEBIDAS ===== */}
      <SettingsSection icon={PhoneOff} title="Chamadas Recebidas">
        <SettingItem
          icon={PhoneOff}
          title="Rejeitar ligações automaticamente"
          description="Rejeita chamadas recebidas e envia uma mensagem automática ao cliente"
          value={callRejectionEnabled}
          onChange={handleCallRejectionEnabled}
          toggle
          divider={callRejectionEnabled === "enabled"}
        />
        <Collapse in={callRejectionEnabled === "enabled"} unmountOnExit>
          <Box
            sx={{
              mx: 3,
              mb: 2,
              mt: 0.5,
              p: 2.5,
              borderRadius: "12px",
              background: isDark ? "rgba(39, 39, 42, 0.4)" : "#f8fafc",
              border: isDark
                ? "1px solid rgba(91, 124, 255, 0.08)"
                : "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: isDark ? "#FAFAFA" : "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MessageSquare size={16} strokeWidth={1.6} style={{ opacity: 0.6 }} />
              Mensagem automática
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: isDark ? "#71717A" : "#94a3b8",
                mb: 1,
              }}
            >
              Texto enviado ao cliente após a chamada ser rejeitada. Suporta variáveis como{" "}
              <code>{"{{contact.name}}"}</code>.
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              size="small"
              value={callRejectionMessage}
              onChange={handleCallRejectionMessageChange}
              onBlur={handleCallRejectionMessageBlur}
              placeholder={
                "Olá {{contact.name}}! No momento não atendemos ligações por aqui. Por favor, descreva sua dúvida por mensagem de texto que responderemos o mais breve possível."
              }
              sx={fieldSx}
            />
          </Box>
        </Collapse>
      </SettingsSection>

      </Box>

      {/* Coluna DIREITA */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>

      {/* ===== INDICADORES ===== */}
      <SettingsSection icon={BarChart3} title="Indicadores">
        <SettingItem
          icon={ChartGantt}
          title="Indicador de Sem Resposta"
          description="Exibir indicador visual de tickets sem resposta (+10min) no menu lateral"
          value={noResponseIndicator}
          onChange={handleNoResponseIndicator}

          toggle
        />
        <SettingItem
          icon={Clock}
          title="Relógio de Sem Resposta no Ticket"
          description="Exibir ícone de relógio vermelho ao lado do contato quando o cliente está sem resposta há mais de 10 minutos"
          value={unrepliedClockIcon}
          onChange={handleUnrepliedClockIcon}
          toggle
        />
        <Collapse in={scheduleType === "company"} unmountOnExit>
          <SettingItem
            icon={ClockFading}
            title={i18n.t("settings.options.fields.noResponseBusinessHours.title")}
            description={i18n.t("settings.options.fields.noResponseBusinessHours.description")}
            value={noResponseBusinessHours}
            onChange={handleNoResponseBusinessHours}

            divider={false}
            toggle
          />
        </Collapse>
        {scheduleType !== "company" && (
          <Box sx={{ height: 0 }} /> /* keep section clean when collapsed */
        )}
      </SettingsSection>

      {/* ===== NOTIFICAÇÕES ===== */}
      {isAdminProfile && (
        <SettingsSection icon={BellRing} title="Notificações">
          <SettingItem
            icon={BellRing}
            title={i18n.t("settings.options.fields.webNotifications.title")}
            description={i18n.t("settings.options.fields.webNotifications.description")}
            value={webNotifications}
            onChange={handleWebNotifications}

            toggle
            divider
          />

          <SettingItem
            icon={MessagesSquare}
            title={i18n.t("settings.options.fields.internalChatNotifications.title")}
            description={i18n.t("settings.options.fields.internalChatNotifications.description")}
            value={internalChatNotifications}
            onChange={handleInternalChatNotifications}
            toggle
            divider={smsEnabled === "enabled"}
          />

          {/* SMS Alert - toggle principal */}
          <SettingItem
            icon={MessageSquare}
            title="Alerta SMS - Sem Resposta"
            description="Enviar SMS de alerta quando a taxa de sem resposta ultrapassar o limite"
            value={smsEnabled}
            onChange={handleSmsEnabled}
  
            toggle
            divider={smsEnabled === "enabled"}
          />

          {/* SMS sub-configurações (expandem quando ativado) */}
          <Collapse in={smsEnabled === "enabled"} unmountOnExit>
            <Box
              sx={{
                mx: 3,
                mb: 2,
                mt: 0.5,
                p: 2.5,
                borderRadius: "12px",
                background: isDark ? "rgba(39, 39, 42, 0.4)" : "#f8fafc",
                border: isDark
                  ? "1px solid rgba(91, 124, 255, 0.08)"
                  : "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              {/* Taxa limite */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: isDark ? "#FAFAFA" : "#1e293b",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Gauge size={16} strokeWidth={1.6} style={{ opacity: 0.6 }} />
                    Taxa Limite (%)
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: isDark ? "#71717A" : "#94a3b8",
                      mt: 0.25,
                    }}
                  >
                    Alerta enviado quando a taxa ultrapassar este valor
                  </Typography>
                </Box>
                <TextField
                  type="number"
                  value={smsThreshold}
                  onChange={handleSmsThreshold}
                  inputProps={{ min: 1, max: 100 }}
                  variant="outlined"
                  size="small"
                  sx={{ width: 90, ...fieldSx }}
                />
              </Box>

              {/* Frequência */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: isDark ? "#FAFAFA" : "#1e293b",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Timer size={16} strokeWidth={1.6} style={{ opacity: 0.6 }} />
                    Frequência de Envio
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: isDark ? "#71717A" : "#94a3b8",
                      mt: 0.25,
                    }}
                  >
                    Intervalo mínimo entre cada alerta
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    type="number"
                    value={smsIntervalValue}
                    onChange={handleSmsIntervalValue}
                    inputProps={{ min: 1 }}
                    variant="outlined"
                    size="small"
                    sx={{ width: 72, ...fieldSx }}
                  />
                  <Select
                    value={smsIntervalUnit}
                    onChange={handleSmsIntervalUnit}
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 110,
                      fontSize: "0.85rem",
                      borderRadius: "10px",
                      color: isDark ? "#FAFAFA" : "#1e293b",
                      backgroundColor: isDark ? "rgba(39, 39, 42, 0.6)" : "#fff",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: isDark ? "rgba(91, 124, 255, 0.2)" : "#e2e8f0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: isDark ? "rgba(91, 124, 255, 0.5)" : "#3D5CFD",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#5B7CFF",
                      },
                      "& .MuiSvgIcon-root": {
                        color: isDark ? "#A1A1AA" : "#64748b",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: isDark ? "#1C1C1F" : "#ffffff",
                          borderRadius: "10px",
                          border: isDark
                            ? "1px solid rgba(91, 124, 255, 0.3)"
                            : "1px solid #e2e8f0",
                          boxShadow: isDark
                            ? "0 8px 24px rgba(0, 0, 0, 0.5)"
                            : "0 4px 12px rgba(0, 0, 0, 0.1)",
                          "& .MuiMenuItem-root": {
                            color: isDark ? "#F5F5F5" : "#1e293b",
                            fontSize: "0.85rem",
                            borderRadius: "6px",
                            mx: 0.5,
                            transition: "background-color 0.15s ease",
                            "&:hover": {
                              backgroundColor: isDark
                                ? "rgba(91, 124, 255, 0.15)"
                                : "rgba(61, 92, 253, 0.08)",
                            },
                            "&.Mui-selected": {
                              backgroundColor: isDark
                                ? "rgba(91, 124, 255, 0.25)"
                                : "rgba(61, 92, 253, 0.12)",
                            },
                          },
                        },
                      },
                    }}
                  >
                    {INTERVAL_UNITS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>

              {/* Telefones */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: isDark ? "#FAFAFA" : "#1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Phone size={16} strokeWidth={1.6} style={{ opacity: 0.6 }} />
                  Telefones Destino
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: isDark ? "#71717A" : "#94a3b8",
                    mb: 1,
                  }}
                >
                  Números que receberão o alerta (um por linha)
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={smsPhones}
                  onChange={handlePhonesChange}
                  onBlur={handlePhonesBlur}
                  placeholder={"+55 (11) 99999-9999\n+55 (21) 98888-8888"}
                  sx={fieldSx}
                />
              </Box>

              {/* Botão testar */}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleTestSMS}
                  disabled={testLoading}
                  startIcon={
                    testLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Send size={16} />
                    )
                  }
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    px: 2.5,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  {testLoading ? "Enviando..." : "Enviar SMS de Teste"}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </SettingsSection>
      )}

      </Box>
    </Box>
  );
}
